import { useState } from "react";
import { type ApolloError } from "@apollo/client";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

export const useCreateUser = () => {
    const { t } = useTranslation("common", {
        keyPrefix: "create-user",
    });

    const [loading] = useState(false);
    const [error] = useState<ApolloError>();

    const schema = yup
        .object({
            user: yup.object().shape({
                email: yup.string().required().email().label(t("email")),
                firstName: yup.string().required().label(t("first-name")),
                lastName: yup.string().required().label(t("last-name")),
                password: yup
                    .string()
                    .min(10)
                    .max(64)
                    .test(
                        "password-not-email",
                        t("password-email-match"),
                        function (value) {
                            const { email } = this.parent; // Access email from the parent object
                            return value !== email; // Password should not match email
                        }
                    )
                    .matches(/[A-Z]/, t("password-uppercase")) // at least one uppercase
                    .matches(/[^a-zA-Z0-9]/, t("password-special")) // at least one special character
                    .matches(/\d/, t("password-number")) // at least one number
                    .matches(/[a-z]/, t("password-lowercase")) // at least one lowercase
                    .test(
                        "at-least-one-number",
                        t("password-number"),
                        (value) => /\d/.test(value || "")
                    )
                    .required()
                    .label(t("password")),
                role: yup.string().required().label(t("role")),
            }),
        })
        .required();

    const form = useForm<yup.InferType<typeof schema>>({
        resolver: yupResolver(schema),
        reValidateMode: "onChange",
        mode: "onChange",
    });

    return {
        form,
        loading,
        error,
    };
};
